import React, { useState } from "react";
import { compose, branch } from "recompose";
import PropTypes from "prop-types";
import {
  Text,
  Textarea,
  Email,
  RadioGroup,
  Radio,
} from "theme/components/atoms/Form/Input";
import Form from "theme/components/atoms/Form/Form";
import config from "config/website";
import FormItem from "theme/components/molecules/Form/Item";
import { defineMessages, useIntl } from "react-intl";

const messages = defineMessages({
  emailErrorRequired: {
    id: "modules.User.LoginForm.email.errorRequired",
    defaultMessage: "Please enter an email",
  },
  amountError: {
    id: "modules.ProductView.GiftCardSynthesis.amountError",
    defaultMessage:
      "Your amount must be between 20€ and 2000€ and must be numeric",
  },
  emailErrorMalformed: {
    id: "modules.User.LoginForm.email.errorMalformed",
    defaultMessage: "Please enter a valid email address",
  },
  am_giftcard_amount: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_amount",
    defaultMessage: "Choose the amount",
  },
  am_giftcard_amount_custom: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_amount_custom",
    defaultMessage: "Gift card amount",
  },
  am_giftcard_image: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_image",
    defaultMessage: "Gift card image",
  },
  am_giftcard_sender_name: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_sender_name",
    defaultMessage: "Gift card sender name",
  },
  am_giftcard_sender_email: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_sender_email",
    defaultMessage: "My email address *",
  },
  am_giftcard_sender_email_placeholder: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_sender_email_placeholder",
    defaultMessage: "My email address *",
  },
  am_giftcard_sender_email_confirm: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_sender_email_confirm",
    defaultMessage: "Gift card sender email confirmation",
  },
  am_giftcard_sender_email_confirm_placeholder: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_sender_email_confirm_placeholder",
    defaultMessage: "Confirm my email address",
  },
  am_giftcard_recipient_email_placeholder: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_recipient_email_placeholder",
    defaultMessage: "Recipient's email address *",
  },
  am_giftcard_recipient_email_confirm: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_recipient_email_confirm",
    defaultMessage: "Gift card recipient email confirmation",
  },
  am_giftcard_recipient_email_confirm_placeholder: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_recipient_email_confirm_placeholder",
    defaultMessage: "Recipient's email address confirmation*",
  },
  am_giftcard_recipient_name: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_recipient_name",
    defaultMessage: "Gift card recipient name",
  },
  am_giftcard_recipient_email: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_recipient_email",
    defaultMessage: "Gift card recipient email",
  },
  am_giftcard_message: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_message",
    defaultMessage: "Gift card message",
  },
  am_giftcard_amount_custom_placeholder: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_amount_custom_placeholder",
    defaultMessage: "Enter an amount (from 10 to 2000€)*",
  },
  am_giftcard_sender_name_placeholder: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_sender_name_placeholder",
    defaultMessage: "Firstname *",
  },
  am_giftcard_recipient_name_placeholder: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_recipient_name_placeholder",
    defaultMessage: "Firstname *",
  },
  am_giftcard_message_placeholder: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_message_placeholder",
    defaultMessage:
      "Your optional message (250 characters maximum, cannot be modified once your e-card has been added to the cart)",
  },
  am_giftcard_email_checkbox: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_email_checkbox",
    defaultMessage: "email checkbox",
  },
  am_giftcard_email_checkbox_myself: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_email_checkbox_myself",
    defaultMessage: "Send by email to my address",
  },
  am_giftcard_email_checkbox_recipient: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_email_checkbox_recipient",
    defaultMessage: "Send by email to the recipient's address",
  },
  am_giftcard_message_required: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_message_required",
    defaultMessage: "This field is required",
  },
  am_giftcard_email_validation: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_email_validation",
    defaultMessage: "Not same email",
  },
  am_giftcard_error_name: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_error_name",
    defaultMessage: "Please enter the first name",
  },
  am_giftcard_error_amount: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_error_amount",
    defaultMessage: "Please enter the amount",
  },
  am_giftcard_error_email: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_error_email",
    defaultMessage: "Please enter the email",
  },
  am_giftcard_error_email_confirm: {
    id: "modules.ProductView.GiftCardSynthesis.am_giftcard_error_email_confirm",
    defaultMessage: "Please confirm the email",
  },
});

const suggestedAmounts = [30, 50, 70, 90, 100];
const GiftCardSynthesis = ({ product, setCustomOption, giftCardFormRef }) => {
  const intl = useIntl();
  const [amount, setAmount] = useState(null);
  const [typeEmail, setTypeEmail] = useState("own");

  const resetActiveClass = (event) => {
    setAmount(event.target.value);
    document
      .querySelectorAll(".giftcard-synthesis__amount-.span")
      .forEach((element) => {
        element.classList.remove("active");
      });
  };

  return (
    <div className="giftcard-synthesis">
      <Form
        setRef={(form) => (giftCardFormRef.current = form)}
        onChange={(data) => {
          if (typeEmail !== data["am_giftcard_email_checkbox"]) {
            setTypeEmail(data["am_giftcard_email_checkbox"]);
          }
          config.giftCardFields.forEach((elem) => {
            if (elem.value_from) {
              setCustomOption(elem.name, data[elem.value_from]?.toString());
            } else {
              setCustomOption(elem.name, data[elem.name]?.toString() ?? "");
            }
          });
        }}
      >
        <div class="giftcard-synthesis__amount-choose">
          {suggestedAmounts.map((suggestAmount) => (
            <span
              key={suggestAmount}
              className={amount === suggestAmount ? "active" : ""}
              onClick={() => {
                setAmount(suggestAmount);
              }}
            >
              {`${suggestAmount}€`}
            </span>
          ))}
        </div>
        {config.giftCardFields.map((field) => {
          if (field.name === "am_giftcard_amount_custom") {
            return (
              <FormItem
                label={intl.formatMessage(messages[field.name])}
                required
              >
                <Text
                  name={field.name}
                  placeholder={intl.formatMessage(
                    messages[`${field.name}_placeholder`]
                  )}
                  onClick={(event) => {
                    resetActiveClass(event);
                  }}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  onBlur={(event) => {
                    if (event.target.value < 20) {
                      setAmount(20);
                    }
                    if (event.target.value > 2000) {
                      setAmount(2000);
                    }
                  }}
                  value={amount}
                  required
                  validations={{
                    notEmptyValidation: function (_, value) {
                      if (!value) return false;
                      return true; // You can return an error
                    },
                    numericValidation: function (_, value) {
                      if (!value) return false;
                      if (!parseFloat(value)) return false;
                      if (parseFloat(value) < 20 || parseFloat(value) > 2000)
                        return false;
                      return true; // You can return an error
                    },
                  }}
                  validationErrors={{
                    numericValidation: intl.formatMessage(
                      messages["amountError"]
                    ),
                    notEmptyValidation: intl.formatMessage(
                      messages["amountError"]
                    ),
                  }}
                />
              </FormItem>
            );
          }
          if (field.type === "radio") {
            return (
              <RadioGroup
                name="am_giftcard_email_checkbox"
                values={["own", "their"]}
                value={typeEmail}
                required
                renderRadio={(props) => (
                  <Radio
                    {...props}
                    required
                    id={`am_giftcard_email_checkbox-${props.value}`}
                    label={
                      props.value === "own"
                        ? intl.formatMessage(
                            messages["am_giftcard_email_checkbox_myself"]
                          )
                        : intl.formatMessage(
                            messages["am_giftcard_email_checkbox_recipient"]
                          )
                    }
                  />
                )}
              />
            );
          }
          if (field.type === "email" && !field.value_from) {
            return (
              <>
                <FormItem label={""} required>
                  <Email
                    placeholder={
                      typeEmail === "own"
                        ? intl.formatMessage(
                            messages["am_giftcard_sender_email_placeholder"]
                          )
                        : intl.formatMessage(
                            messages["am_giftcard_recipient_email_placeholder"]
                          )
                    }
                    name={field.name}
                    required
                    validationErrors={{
                      required: intl.formatMessage(messages.emailErrorRequired),
                      isEmail: intl.formatMessage(messages.emailErrorMalformed),
                    }}
                  />
                  <Email
                    name={`${field.name}_validation`}
                    placeholder={
                      typeEmail === "own"
                        ? intl.formatMessage(
                            messages[
                              "am_giftcard_sender_email_confirm_placeholder"
                            ]
                          )
                        : intl.formatMessage(
                            messages[
                              "am_giftcard_recipient_email_confirm_placeholder"
                            ]
                          )
                    }
                    required
                    validations={{
                      sameEmailValidation: function (values, value) {
                        if (value !== values[field.name]) {
                          return false;
                        }
                        return true;
                      },
                    }}
                    validationErrors={{
                      sameEmailValidation: intl.formatMessage(
                        messages.am_giftcard_email_validation
                      ),
                    }}
                  />
                </FormItem>
              </>
            );
          }
          if (field.type === "textarea") {
            return (
              <FormItem label={""}>
                <Textarea
                  name={field.name}
                  placeholder={intl.formatMessage(
                    messages[`${field.name}_placeholder`]
                  )}
                  maxLength={250}
                />
              </FormItem>
            );
          }
          if (field.type === "text") {
            return (
              <FormItem
                label={intl.formatMessage(messages[field.name])}
                required
                inline={true}
              >
                <Text
                  required
                  placeholder={intl.formatMessage(
                    messages[`${field.name}_placeholder`]
                  )}
                  name={field.name}
                />
              </FormItem>
            );
          }
          return null;
        })}
        <button
          type="submit"
          id="fake-submit-giftcard"
          style={{ display: "none" }}
        >
          Submit
        </button>
      </Form>
    </div>
  );
};

GiftCardSynthesis.propTypes = {
  product: PropTypes.object.isRequired,
  setCustomOption: PropTypes.func.isRequired,
};

export default compose(
  branch(
    (props) => props.product.isGiftCard && config.giftCardFields?.length > 0,
    (BaseComponent) => BaseComponent,
    () => () => null
  )
)(GiftCardSynthesis);
